import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_lead-tracker-96/artifacts/g69u3ad3_D%20Nexus%20Logo.png";

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-white border-b shadow-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/login" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </Link>
          <img src={LOGO_URL} alt="Dnexus Media" className="h-10" />
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 md:p-12">
          <h1 className="text-3xl font-bold text-slate-900 mb-2">Terms of Service</h1>
          <p className="text-slate-500 mb-8">Last updated: February 21, 2026</p>

          <div className="prose prose-slate max-w-none">
            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">1. Agreement to Terms</h2>
            <p className="text-slate-600 mb-4">
              By accessing or using the Dnexus Media CRM platform ("Service"), you agree to be bound by these Terms of Service 
              ("Terms"). If you disagree with any part of the terms, you may not access the Service.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">2. Description of Service</h2>
            <p className="text-slate-600 mb-4">
              Dnexus Media CRM is a customer relationship management platform that provides:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Lead management and tracking</li>
              <li>Contact and client management</li>
              <li>Task and calendar management</li>
              <li>Integration with third-party services (Facebook, WhatsApp, Google Sheets, Email)</li>
              <li>Reporting and analytics</li>
              <li>Team collaboration features</li>
              <li>Attendance and HR management</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">3. User Accounts</h2>
            <p className="text-slate-600 mb-4">
              When you create an account with us, you must provide accurate, complete, and current information. 
              You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Safeguarding your password and account credentials</li>
              <li>All activities that occur under your account</li>
              <li>Notifying us immediately of any unauthorized access</li>
              <li>Ensuring your account information remains accurate and up-to-date</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">4. Acceptable Use</h2>
            <p className="text-slate-600 mb-4">You agree not to use the Service to:</p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Violate any applicable laws or regulations</li>
              <li>Infringe on intellectual property rights of others</li>
              <li>Transmit harmful, offensive, or illegal content</li>
              <li>Attempt to gain unauthorized access to the Service or other systems</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Send spam or unsolicited communications</li>
              <li>Collect user information without consent</li>
              <li>Impersonate any person or entity</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">5. Data and Content</h2>
            <p className="text-slate-600 mb-4">
              You retain ownership of all data and content you upload to the Service. By using the Service, you grant us 
              a license to use, store, and process your data solely to provide and improve the Service.
            </p>
            <p className="text-slate-600 mb-4">
              You are responsible for:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Ensuring you have the right to upload and use the data</li>
              <li>Maintaining appropriate backups of your data</li>
              <li>Complying with data protection laws regarding personal data you process</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">6. Third-Party Integrations</h2>
            <p className="text-slate-600 mb-4">
              The Service allows integration with third-party services. When using these integrations:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>You must comply with the terms of service of each third-party platform</li>
              <li>We are not responsible for the availability or functionality of third-party services</li>
              <li>You authorize us to access and process data from connected services as needed</li>
              <li>You can disconnect integrations at any time</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">7. Intellectual Property</h2>
            <p className="text-slate-600 mb-4">
              The Service and its original content, features, and functionality are owned by Dnexus Media and are 
              protected by international copyright, trademark, patent, trade secret, and other intellectual property laws.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">8. Termination</h2>
            <p className="text-slate-600 mb-4">
              We may terminate or suspend your account immediately, without prior notice or liability, for any reason, 
              including if you breach the Terms. Upon termination:
            </p>
            <ul className="list-disc pl-6 text-slate-600 mb-4 space-y-2">
              <li>Your right to use the Service will immediately cease</li>
              <li>You may request export of your data within 30 days</li>
              <li>We may delete your data after a reasonable retention period</li>
            </ul>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">9. Limitation of Liability</h2>
            <p className="text-slate-600 mb-4">
              In no event shall Dnexus Media, its directors, employees, partners, agents, suppliers, or affiliates be 
              liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, 
              loss of profits, data, use, goodwill, or other intangible losses.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">10. Disclaimer</h2>
            <p className="text-slate-600 mb-4">
              The Service is provided on an "AS IS" and "AS AVAILABLE" basis. We make no warranties, expressed or implied, 
              regarding the Service's operation or the information, content, or materials included therein.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">11. Indemnification</h2>
            <p className="text-slate-600 mb-4">
              You agree to defend, indemnify, and hold harmless Dnexus Media and its affiliates from any claims, damages, 
              obligations, losses, liabilities, costs, or debt arising from your use of the Service or violation of these Terms.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">12. Governing Law</h2>
            <p className="text-slate-600 mb-4">
              These Terms shall be governed by and construed in accordance with the laws of India, without regard to its 
              conflict of law provisions.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">13. Changes to Terms</h2>
            <p className="text-slate-600 mb-4">
              We reserve the right to modify or replace these Terms at any time. We will provide notice of any material 
              changes by posting the new Terms on this page and updating the "Last updated" date.
            </p>

            <h2 className="text-xl font-semibold text-slate-800 mt-8 mb-4">14. Contact Us</h2>
            <p className="text-slate-600 mb-4">
              If you have any questions about these Terms, please contact us at:
            </p>
            <div className="bg-slate-50 p-4 rounded-lg text-slate-600">
              <p><strong>Dnexus Media</strong></p>
              <p>Email: legal@dnexusmedia.com</p>
              <p>Website: https://crm.dnexusmedia.com</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-slate-500 text-sm">
          © {new Date().getFullYear()} Dnexus Media. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
